import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ContextQuestion } from '@/lib/contextQuestion';
import { cn } from '@/lib/utils';

interface ContextQuestionFormProps {
  question: ContextQuestion;
  onSubmit: (answers: Record<string, string>) => void;
  isDisabled?: boolean;
}

export function ContextQuestionForm({ question, onSubmit, isDisabled }: ContextQuestionFormProps) {
  // Initialize answers with default values from fields
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    question.fields.forEach(field => {
      if (field.defaultValue) {
        initial[field.id] = field.defaultValue;
      }
    });
    return initial;
  });

  const handleFieldChange = (fieldId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = () => {
    // Check required fields
    const allRequiredFilled = question.fields.every(
      field => !field.required || answers[field.id]?.trim()
    );
    if (allRequiredFilled) {
      // Merge hidden data with user answers
      const fullAnswers = {
        ...question.hidden_data,
        ...answers,
      };
      onSubmit(fullAnswers);
    }
  };

  const allRequiredFilled = question.fields.every(
    field => !field.required || answers[field.id]?.trim()
  );

  return (
    <div className="mt-3 p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-4">
      <div className="space-y-3">
        {question.fields.map((field) => (
          <div key={field.id} className="space-y-1.5">
            <Label htmlFor={field.id} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            
            {field.type === 'select' && field.options && (
              <Select
                value={answers[field.id] || ''}
                onValueChange={(value) => handleFieldChange(field.id, value)}
                disabled={isDisabled}
              >
                <SelectTrigger 
                  id={field.id}
                  className={cn(
                    "w-full bg-background",
                    !answers[field.id] && "text-muted-foreground"
                  )}
                >
                  <SelectValue placeholder={field.placeholder || 'Select an option'} />
                </SelectTrigger>
                <SelectContent className="bg-popover z-[100]">
                  {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {field.type === 'text' && (
              <Input
                id={field.id}
                value={answers[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                disabled={isDisabled}
                className="bg-background"
              />
            )}

            {field.type === 'date' && (
              <Input
                id={field.id}
                type="date"
                value={answers[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                disabled={isDisabled}
                className="bg-background"
              />
            )}
          </div>
        ))}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!allRequiredFilled || isDisabled}
        size="sm"
        className="w-full gap-2"
      >
        <Send className="h-4 w-4" />
        Submit
      </Button>
    </div>
  );
}
